import getSymbolFromCurrency from "currency-symbol-map";
import {
  CURRENCY_CODES,
  DEFAULT_CURRENCY_CODE,
  getCountryByCode,
  getCurrencyForCountry,
  getNumberingSystemForCurrency,
  resolveCountryCode,
} from "../data/countries";

const toLocalISODateString = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

export const parseDateString = (input) => {
  if (!input) return null;

  if (input instanceof Date) {
    return isNaN(input.getTime()) ? null : input;
  }

  const dateString = String(input);
  let date;

  // Case 1: ISO format
  if (dateString.includes("T") || dateString.includes("-")) {
    const isoDateOnly = /^(\d{4})-(\d{2})-(\d{2})$/;
    const match = dateString.match(isoDateOnly);

    if (match) {
      const [, y, m, d] = match;
      date = new Date(Number(y), Number(m) - 1, Number(d));
    } else {
      date = new Date(dateString);
    }
  }
  // Case 2: DD/MM/YY or DD/MM/YYYY
  else if (dateString.includes("/")) {
    const [day, month, yearRaw] = dateString.split("/");

    let year = yearRaw;

    // convert 2-digit year → 20YY
    if (year.length === 2) {
      year = "20" + year;
    }

    date = new Date(Number(year), Number(month) - 1, Number(day));
  }

  if (!date || isNaN(date.getTime())) {
    return null;
  }

  return date;
};

export const formatDateDDMMYYYY = (dateString) => {
  if (!dateString) return "";

  const date = parseDateString(dateString);

  if (!date) {
    console.warn("Invalid date:", dateString);
    return "";
  }

  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();

  return `${day}/${month}/${year}`;
};

export const isAuthenticated = () => {
  return !!localStorage.getItem("authToken");
};

const compactAmount = (value) =>
  value.toFixed(1).replace(/\.0$/, "");

const resolveNumberingSystem = (code) => {
  const country = getCountryByCode(code);
  if (country) return country.numbering;
  return getNumberingSystemForCurrency(code);
};

export const formatBudget = (value, countryOrCurrencyCode) => {
  if (value === null || value === undefined || value === "") return "";

  const num = Number(value);
  if (!Number.isFinite(num)) return String(value);

  // A missing market stays on lakh/crore. Western units apply only when a
  // country or currency is given and that market uses them.
  const numbering = countryOrCurrencyCode
    ? resolveNumberingSystem(countryOrCurrencyCode)
    : "indian";

  if (numbering === "western") {
    if (num >= 1_000_000_000) {
      return `${compactAmount(num / 1_000_000_000)} B`;
    }
    if (num >= 1_000_000) {
      return `${compactAmount(num / 1_000_000)} M`;
    }
    if (num >= 1_000) {
      return `${compactAmount(num / 1_000)} K`;
    }
    return String(num);
  }

  if (num >= 10_000_000) {
    return `${compactAmount(num / 10_000_000)} Cr`;
  }

  if (num >= 100_000) {
    return `${compactAmount(num / 100_000)} L`;
  }

  if (num >= 1_000) {
    return `${compactAmount(num / 1_000)} K`;
  }

  return String(num);
};

export const splitBudgetIntoValueAndUnit = (value, countryOrCurrencyCode) => {
  if (!value && value !== 0) return { value: "", unit: "" };

  const formatted = formatBudget(value, countryOrCurrencyCode);
  const [num, unit] = formatted.split(" ");

  return {
    value: Number(num),
    unit: unit || "",
  };
};

export const convertBudgetToNumber = (value, unit) => {
  if (value === null || value === undefined || value === "") return "";

  if (!unit && typeof value === "string") {
    const match = value
      .trim()
      .toLowerCase()
      .match(/^(\d+(\.\d+)?)\s?(k|l|cr|m|b)?$/);
    if (!match) return "";
    value = match[1];
    unit = match[3];
  }

  const num = Number(value);
  if (isNaN(num)) return "";

  switch ((unit || "").toLowerCase()) {
    case "cr":
      return Math.round(num * 10_000_000);
    case "l":
      return Math.round(num * 100_000);
    case "b":
      return Math.round(num * 1_000_000_000);
    case "m":
      return Math.round(num * 1_000_000);
    case "k":
      return Math.round(num * 1_000);
    default:
      return Math.round(num);
  }
};

const normalizeCurrencyCode = (currencyCode) => {
  const normalized = String(currencyCode || "").trim().toUpperCase();
  return CURRENCY_CODES.includes(normalized) ? normalized : DEFAULT_CURRENCY_CODE;
};

export const resolvePropertyCurrency = (property = {}) => {
  const normalized = String(property?.currency || "").trim().toUpperCase();
  if (CURRENCY_CODES.includes(normalized)) return normalized;
  return getCurrencyForCountry(resolveCountryCode(property));
};

export const getCurrencySymbol = (currencyCode) => {
  const currency = normalizeCurrencyCode(currencyCode);
  if (currency === "AED") return "AED";
  const fromPackage = getSymbolFromCurrency(currency);
  if (fromPackage) return fromPackage;

  try {
    const part = new Intl.NumberFormat("en", {
      style: "currency",
      currency,
      currencyDisplay: "narrowSymbol",
    })
      .formatToParts(0)
      .find((item) => item.type === "currency");
    return part?.value || currency;
  } catch {
    return currency;
  }
};

const moneySpacer = (symbol) => (String(symbol).length > 1 ? " " : "");

export const formatMoney = (value, currencyCode) => {
  if (value === null || value === undefined || value === "") return "—";
  const currency = normalizeCurrencyCode(currencyCode);
  const amount = formatBudget(value, currency);
  if (!amount) return "—";
  const symbol = getCurrencySymbol(currency);
  return `${symbol}${moneySpacer(symbol)}${amount}`;
};

export const formatINR = (value) => {
  if (value === null || value === undefined || value === "") return "—";
  const num = Number(value);
  if (Number.isNaN(num)) return `₹${String(value)}`;
  return `₹${num.toLocaleString("en-IN")}`;
};

export const priceRangeLabel = (
  min_budget,
  max_budget,
  price_range,
  currencyCode = DEFAULT_CURRENCY_CODE,
) => {
  const hasMin = min_budget !== undefined && min_budget !== null && min_budget !== "";
  const hasMax = max_budget !== undefined && max_budget !== null && max_budget !== "";

  if (hasMin && hasMax) {
    return `${formatMoney(min_budget, currencyCode)} - ${formatMoney(max_budget, currencyCode)}`;
  }

  return price_range || "—";
};

export { resolveCountryCode };

export const formatTimeline = (value) => {
  if (!value) return "-";

  return value
    .replace(/_/g, " ")                 // 1_month → 1 month
    .replace(/\b\w/g, (c) => c.toUpperCase()); // → 1 Month
};



export const formatDateAPIFormat = (date) => {
  if (!date) return "";

  let d;

  // If already Date object
  if (date instanceof Date) {
    d = date;
  }

  // DD/MM/YYYY or DD-MM-YYYY or DD-MM-YY
  else if (typeof date === "string") {
    const normalized = date.replace(/-/g, "/"); // unify separator
    const parts = normalized.split("/");

    if (parts.length === 3) {
      let [day, month, year] = parts;

      // handle 2-digit year
      if (year.length === 2) {
        year = "20" + year;
      }

      return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
    }

    d = new Date(date);
  }

  else {
    d = new Date(date);
  }

  if (isNaN(d.getTime())) return "";

  return toLocalISODateString(d);
};


export function formatDateTime(dateString) {
  if (!dateString) return "";

  const date = new Date(dateString);

  return date.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
}

  // helper to normalize values
  export const normalize = (val) => (val || "").toString().trim().toLowerCase();